/** The brand mark, inlined into the DOM rather than referenced as an <img>.
 *
 * Inlining is what makes `stroke="currentColor"` work: the artwork's drawn ink (#2C3E50)
 * scores 1.64:1 against the sidebar and is invisible there, so the mark has to inherit
 * its slot's colour the way the nav icons in ./icons.ts already do. An <img> cannot.
 *
 * Same DOMParser approach as ./icons.ts, and the same rule applies — the markup comes
 * from trusted build-time literals only, never from data. */
import headSource from "../assets/logo-head.svg?raw";
import markSource from "../assets/logo-mark.svg?raw";

/** `mark` is the full owl (0.57 aspect, needs vertical room); `head` is the compact
 * crop for anywhere that would otherwise render it too small to read. */
export type LogoVariant = "mark" | "head";

const SOURCES: Record<LogoVariant, string> = { mark: markSource, head: headSource };

export interface LogoOptions {
  className?: string;
  /** True when visible text alongside already names the brand — then the mark is
   * decoration and repeating "Owlish Beans" to a screen reader is just noise. */
  decorative?: boolean;
}

export function logo(variant: LogoVariant, options: LogoOptions = {}): SVGElement {
  const doc = new DOMParser().parseFromString(SOURCES[variant], "image/svg+xml");
  const svg = document.importNode(doc.documentElement, true) as unknown as SVGElement;
  svg.setAttribute("class", options.className ?? "logo");
  if (options.decorative) {
    svg.removeAttribute("role");
    svg.removeAttribute("aria-label");
    svg.setAttribute("aria-hidden", "true");
  }
  return svg;
}

/** Mark + wordmark. The wordmark stays real text rather than being baked into the SVG,
 * so it inherits the app's font and stays selectable and translatable. */
export function brandLockup(variant: LogoVariant, className: string): DocumentFragment {
  const fragment = document.createDocumentFragment();
  fragment.appendChild(logo(variant, { className: `${className}-mark`, decorative: true }));
  const word = document.createElement("span");
  word.className = `${className}-word`;
  word.textContent = "Owlish Beans";
  fragment.appendChild(word);
  return fragment;
}
